import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

import { BsChevronDoubleUp } from "react-icons/bs";

import { Container, Box } from '@mui/material';

import Nav from './Nav';
import Home from './Home';
import Experience from './Experience';
import Videos from './Videos';
import Projects from './Projects';
import Contact from './Contact';
import PageNotFound from './404';
import './App.css';

function App() {
  const [showButton, setShowButton] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('portfolio-theme') === 'dark');
  const mainRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem('portfolio-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const themeClass = darkMode ? 'theme-dark' : 'theme-light';
    const oppositeThemeClass = darkMode ? 'theme-light' : 'theme-dark';

    document.documentElement.classList.add(themeClass);
    document.documentElement.classList.remove(oppositeThemeClass);
    document.body.classList.add(themeClass);
    document.body.classList.remove(oppositeThemeClass);

    return () => {
      document.documentElement.classList.remove(themeClass);
      document.body.classList.remove(themeClass);
    };
  }, [darkMode]);

  useEffect(() => {
    const mainElement = mainRef.current;
    if (!mainElement) return;

    const handleScroll = () => {
      const threshold = 0.02 * mainElement.scrollHeight;

      if (mainElement.scrollTop > threshold) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0; // Instant scroll to top on route change
    }
  }, [location]);

  const scrollToTop = () => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'smooth' }); // Smooth scroll for button
    }
  };

  return (
    <Container className={`app-shell ${darkMode ? 'theme-dark' : 'theme-light'}`} sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      backgroundColor: darkMode ? '#071c33' : '#f7f7f7',
      minWidth: '100vw',
      height: '100vh',
      display: 'grid',
      gridTemplateRows: 'auto minmax(0, 1fr)',
      overflow: 'hidden',
      m: 0,
      p: '0 !important'
    }}>
      <Box
        sx={{
          borderBottom: `0.15rem solid ${darkMode ? 'rgba(113, 137, 166, 0.55)' : '#f7f7f7'}`,
          position: 'relative',
          zIndex: 2,
          m: 0
        }}
      >
        <Nav
          currentPath={location.pathname.split('/').filter(Boolean)[0] || ''}
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode((current) => !current)}
        />
      </Box>
      <main
        className={`fade-in ${location.pathname === '/projects' ? 'projects-route-main' : ''}`}
        ref={mainRef}
        style={{ overflowY: location.pathname === '/projects' ? 'hidden' : 'auto', maxHeight: 'none', minHeight: 0 }}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/experience" element={<Experience />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/contact" element={<Contact />} />

          {/* Catch only unknown paths */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </main>
      {showButton && (
        <button onClick={scrollToTop} className="back-to-top" title="Go to top">
          <BsChevronDoubleUp />
        </button>
      )}
    </Container>
  );
}

export default App;
